import {
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { IImageInfo, IResponseProjectData } from "@types";
import { useEffect, useRef, useState } from "react";
import { projectImagesInfoState, selectedImageState, dataLoadingState } from "../../recoil";
import { useRecoilState } from "recoil";
import { getProject, getProjectNG, getProjectLastLabelSeq } from "../../services/projectService";
import { formatNumber } from "../../utils/utils";
import { toast } from "react-toastify";
import { t } from "i18next";
import { useViewportDetector } from "./ProjectImageTableFunc";
import { org_keyMap } from "../../keyMapData/keyData";
import { extractFileName, truncateString } from "common-lib";

// 프로젝트의 이미지에 대한 데이터 조회 (전체)
export async function fetchImageInfo(selectedProject: number, page?: number) {
  try {
    const response = await getProject(selectedProject, undefined, 1);
    const imagesData = response.project.images;
    return { imagesData };
  } catch (error) {
    console.error(error);
  }
}

interface ProjectImageTableProps {
  id: number;
  projectData: IResponseProjectData;
  userConfig: any;
  label: string;
  inspResult: any[];
  setAiLabelList: (aiLabel: any) => void;
  inspStatus: InspStatusItem | null;
}

interface ImageInfoItem {
  AILabel: string;
  ai_update_at: string;
  id: number;
  label: string;
  path: string;
  recentLabel: string;
  score: number;
  seq: number;
}

export default function ProjectImageTable({
  id,
  projectData,
  userConfig,
  label,
  inspResult,
  setAiLabelList,
  inspStatus,
}: ProjectImageTableProps) {
  const [selectedRow, setSelectedRow] = useRecoilState(selectedImageState);
  const [projectImagesInfo, setProjectImagesInfo] = useRecoilState(projectImagesInfoState);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<string>("");
  const [projectNGData, setProjectNGData] = useState<any[]>([]);
  const [isNewData, setIsNewData] = useState(false);
  const [isOldData, setIsOldData] = useState(false);
  const observerTopTargetRef = useRef<HTMLDivElement>(null);
  const observerBottomTargetRef = useRef<HTMLDivElement>(null);
  const [isThrottled, setIsThrottled] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isKeyHandling, setIsKeyHandling] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const prevSelectedRowIdRef = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [prevPage, setPrevPage] = useState<number | null>(null);
  const [isTimeOut, setIsTimeOut] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useRecoilState(dataLoadingState);

  // test
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const rowRef = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const response = await getProjectLastLabelSeq(id, label);
      const seq = response.seq;
      const index = response.index + 1;
      const currentUrl = window.location.href;

      let targetPage = 1;
      if (!currentUrl.includes("analysis")) {
        targetPage = Math.floor((seq - 1) / size) + 1;
        if (targetPage < 1) {
          targetPage = 1;
        }
      } else {
        //분석일때
        targetPage = Math.floor((index - 1) / size) + 1;
        if (targetPage < 1) {
          targetPage = 1;
        }
      }
      const data = await getProject(id, targetPage, size, label);
      const imagesData = data.project.images as ImageInfoItem[];
      setStep(data.project.step);
      setProjectImagesInfo(imagesData);
      setPage(targetPage);
      setPrevPage(targetPage);
      setIsFirstLoad(true);
      setLoading(false);

      const aiLabelList = imagesData.map(({ id, AILabel, score }) => ({
        id,
        AILabel,
        score,
      }));
      setAiLabelList(aiLabelList);
      setIsLoadingData(true);
    };
    fetchData();

    setTimeout(() => {
      setIsTimeOut(true);
    }, 3000);
  }, []);

  useEffect(() => {
    if (projectImagesInfo.length === 0) return;
    if (projectImagesInfo.length > 100) return;
    const validRows = projectImagesInfo.filter((row) => row.label === "");
    // if (validRows.length === 0) return;
    let lastLabeled;
    if (validRows.length === 0) {
      lastLabeled = projectImagesInfo[0];
    } else {
      lastLabeled = validRows[0];
    }

    if (!lastLabeled || lastLabeled.seq === undefined) return;

    const nextSeq = lastLabeled.seq;
    // const nextRow = projectImagesInfo.find((row) => row.seq === nextSeq);

    if (lastLabeled) {
      setSelectedRow(lastLabeled);
      setTimeout(() => {
        scrollToRow(nextSeq);
      }, 0);
    }
  }, [projectImagesInfo]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedRow, isKeyHandling]);

  // row 변경시 스크롤 조정
  useEffect(() => {
    if (!selectedRow) return;

    const prevId = prevSelectedRowIdRef.current;
    const currentId = selectedRow.id;

    const rowEl = rowRefs.current[String(currentId)];
    const container = document.getElementById("table-container") as HTMLElement | null;

    if (!container || !rowEl || !currentId) return;

    const isMovingUp = prevId !== null && currentId < prevId;

    requestAnimationFrame(() => {
      const rowTop = rowEl.offsetTop;
      const rowBottom = rowTop + rowEl.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      const isVisible = rowTop >= containerScrollTop && rowBottom <= containerScrollTop + containerHeight;

      if (isMovingUp) {
        if (!isVisible || rowTop < containerScrollTop + 100) {
          container.scrollTo({
            top: rowTop - 100,
            behavior: "smooth",
          });
        }
      } else {
        container.scrollTo({
          top: rowTop - 700,
          behavior: "smooth",
        });
      }
    });

    prevSelectedRowIdRef.current = currentId;
  }, [selectedRow]);

  useEffect(() => {
    const preventScroll = (e: KeyboardEvent) => {
      const { prevKey, nextKey, confirmKey } = myControlKey();
      const keysToBlock = [org_keyMap[prevKey], org_keyMap[nextKey]].filter(Boolean);

      if (keysToBlock.includes(e.key)) {
        e.preventDefault();
      }
    };

    const container = tableRef.current;

    if (container) {
      container.addEventListener("keydown", preventScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("keydown", preventScroll);
      }
    };
  }, []);

  useEffect(() => {
    setStep(projectData.step);
  }, []);

  // 프로젝트 NG 데이터 조회
  // useEffect(() => {
  //   const fetchProjectNGData = async () => {
  //     const response = await getProjectNG(id);
  //     const ngData = response.ngData;
  //   };
  //   fetchProjectNGData();
  // }, []);

  const [visibleAIIndices, setVisibleAIIndices] = useState<number[]>([]);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    const newItemsCount = projectImagesInfo.length - prevLengthRef.current;

    if (inspStatus?.state === "end" || !inspStatus) {
      // 애니메이션 없이 전체 인덱스 한 번에 보여줌
      const allIndices = projectImagesInfo.map((_, i) => i);
      setVisibleAIIndices(allIndices);
      prevLengthRef.current = projectImagesInfo.length;
      return;
    }

    if (newItemsCount > 0) {
      const start = prevLengthRef.current;
      const end = projectImagesInfo.length;

      for (let i = start; i < end; i++) {
        setTimeout(
          () => {
            setVisibleAIIndices((prev) => [...prev, i]);
          },
          (i - start) * 300,
        );
      }

      prevLengthRef.current = projectImagesInfo.length;
    }
  }, [projectImagesInfo, inspStatus?.state]);

  useEffect(() => {
    if (isNewData) {
      if (!isFinite(page) || page === prevPage) return;
      try {
        const newData = async () => {
          const data = await getProject(id, page, size, label);
          const imagesData = data.project.images as ImageInfoItem[];

          if (!imagesData || imagesData.length < size) {
            setHasNextPage(false);
          }

          const newImages = imagesData.filter(
            (newImg: IImageInfo) => !images.some((existingImg) => existingImg.id === newImg.id),
          );

          const aiLabelList = imagesData.map(({ id, AILabel, score }) => ({
            id,
            AILabel,
            score,
          }));

          const combined = [...inspResult, ...aiLabelList];

          if (combined.length >= 301) {
            const trimmed = combined.slice(100); //오래된 100개 데이터 제거
            setAiLabelList(trimmed);
          } else {
            setAiLabelList(combined);
          }

          setProjectImagesInfo((prevImages) => {
            const images = [...prevImages, ...newImages];

            // 301개 초과시 가장 오래된 100개 제거
            if (images.length >= 301) {
              return images.slice(100);
            }
            return images;
          });

          setPrevPage(page);

          const container = tableRef.current;
          if (container) {
            container.scrollTop = 10000;
          }
        };

        newData();
      } catch (error) {
        console.error("새로운 데이터 조회 중 오류 발생:", error);
        // toast.error("새로운 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          setIsNewData(false);
        }, 3000);
      }
    }
  }, [isNewData]);

  useEffect(() => {
    if (isOldData && isTimeOut) {
      if (!isFinite(page) || page === prevPage) return;
      try {
        const oldData = async () => {
          setIsLoading(true);
          const data = await getProject(id, page, size, label);
          const imagesData = data.project.images as ImageInfoItem[];
          const aiLabelList = imagesData.map(({ id, AILabel, score }) => ({
            id,
            AILabel,
            score,
          }));

          const combined = [...aiLabelList, ...inspResult];

          if (combined.length >= 301) {
            const trimmed = combined.slice(0, combined.length - 100); // 뒤에서 100개 제거
            setAiLabelList(trimmed);
          } else {
            setAiLabelList(combined);
          }
          // const minSeq = Math.min(...imagesData.map((img: any) => img.seq));
          // const nextRow = imagesData.find((row: any) => row.seq === minSeq);
          // console.log("nextRow", nextRow);
          // setSelectedRow(nextRow);

          setProjectImagesInfo((prevImages) => {
            const slicedPrevImages = prevImages.slice(0, 200);
            return [...imagesData, ...slicedPrevImages];
          });
          setPrevPage(page);
        };

        oldData();

        const container = tableRef.current;
        if (container) {
          container.scrollTop = 150;
        }
      } catch (error) {
        console.error("오래된 데이터 조회 중 오류 발생:", error);
        // toast.error("오래된 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          setIsOldData(false);
        }, 3000);
      }
    }
  }, [isOldData]);

  // 상단 뷰포트 감지
  useViewportDetector({
    targetRef: observerTopTargetRef,
    rootRef: tableRef,
    onIntersect: () => {
      if (loading) {
        return;
      }
      const minSeq = Math.min(...projectImagesInfo.map((img: any) => img.seq));
      const calculatedNextPage = Math.floor((minSeq + 1) / size);
      if (calculatedNextPage < 1) {
        return;
      }
      setPage(calculatedNextPage);
      if (isTimeOut) {
        setIsOldData(true);
      }
    },
  });

  // 하단 뷰포트 감지
  useViewportDetector({
    targetRef: observerBottomTargetRef,
    rootRef: tableRef,
    onIntersect: () => {
      if (loading) return;
      if (!isThrottled && hasNextPage) {
        const maxSeq = Math.max(...projectImagesInfo.map((img: any) => img.seq));
        const calculatedNextPage = Math.floor((maxSeq + 1) / size) + 1;
        setPage(calculatedNextPage);
        setIsNewData(true);
      }
    },
    enabled: !loading && isFirstLoad,
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isKeyHandling || e.key === "Process") return;
    setIsKeyHandling(true);
    const { prevKey, nextKey, confirmKey } = myControlKey();
    const pressedKey = e.key.toLowerCase();
    const currentIndex = projectImagesInfo.findIndex((row) => row.seq === selectedRow.seq);
    if (pressedKey === nextKey) {
      e.preventDefault();
      const nextIndex = currentIndex + 1;
      if (projectImagesInfo[nextIndex]) {
        setSelectedRow(projectImagesInfo[nextIndex]);
      } else {
        toast.warning("마지막 이미지입니다.");
      }
    } else if (pressedKey === prevKey) {
      e.preventDefault();
      const prevIndex = currentIndex - 1;
      if (projectImagesInfo[prevIndex]) {
        setSelectedRow(projectImagesInfo[prevIndex]);
      } else {
        toast.warning("첫 번째 이미지입니다.");
      }
    }
    setTimeout(() => {
      setIsKeyHandling(false);
    }, 100);
  };

  const columns: GridColDef[] = [
    { field: "no", headerName: t("No"), width: 40 },
    {
      field: "imageName",
      headerName: t("FileName"),
      width: 200,
      renderCell: (params: any) => (
        <Tooltip title={params.row.path}>
          <span>{params.row.imageName}</span>
        </Tooltip>
      ),
    },
    { field: "label", headerName: t("User"), width: 125 },
    { field: "AILabel", headerName: t("AI"), width: 125 },
    {
      field: "score",
      headerName: t("Score"),
      width: 150,
      renderCell: (params: any) => (
        <Tooltip title={params.row.path}>
          <span>{formatNumber(Number(params.row.score), 4)}</span>
        </Tooltip>
      ),
    },
  ];

  const handleRowClick = async (row: IImageInfo) => {
    setSelectedRow(row);
  };

  // control key 매핑 함수
  const myControlKey = () => {
    const prevKey = userConfig.controlKey[0].replace(/[{}]/g, "").toLowerCase();
    const nextKey = userConfig.controlKey[1].replace(/[{}]/g, "").toLowerCase();
    const confirmKey = userConfig.controlKey[2].replace(/[{}]/g, "").toLowerCase();
    return { prevKey, nextKey, confirmKey };
  };

  const scrollToRow = (seq: number) => {
    const container = tableRef.current;
    const targetRow = rowRef.current[seq];
    if (container && targetRow) {
      container.scrollTo({
        top: targetRow.offsetTop - 700,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <Grid
        ref={tableRef}
        container
        sx={{
          width: "100%",
          height: "884px",
          overflowY: "auto",
        }}
        id="table-container"
        tabIndex={0}
      >
        <TableContainer>
          <div
            ref={observerTopTargetRef}
            // style={{ height: "10px", border: "solid 1px red" }}
          />
          <Table>
            <TableHead
              sx={{
                bgcolor: "var(--dg-table-header-bg-color)",
              }}
            >
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      color: "var(--dg-table-header-color)",
                      height: "58px",
                    }}
                    className={`steps_3_${index + 1}`}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isFirstLoad ? (
                projectImagesInfo.map((row, index) => (
                  <TableRow
                    key={`${row.id}-${index}`}
                    data-row-id={row.id}
                    ref={(el) => {
                      rowRefs.current[String(row.id)] = el;
                      rowRef.current[String(row.seq)] = el;
                    }}
                    onClick={() => {
                      handleRowClick(row);
                    }}
                    sx={{
                      backgroundColor:
                        selectedRow?.id === row.id ? "var(--selected-row-bg)" : "var(--unselected-row-bg)",
                      cursor: "pointer",
                      color: "var(--dg-color)",
                    }}
                  >
                    <TableCell sx={{ minWidth: "40px", color: "var(--dg-color)" }}>{row.seq}</TableCell>
                    <Tooltip title={row.path} placement="left">
                      <TableCell sx={{ minWidth: "200px", color: "var(--dg-color)" }}>
                        {truncateString(extractFileName(row.path), 20)}
                      </TableCell>
                    </Tooltip>
                    <TableCell sx={{ width: "125px", color: "var(--dg-color)" }}>{row.label}</TableCell>
                    <AITableCell
                      inspStatus={inspStatus}
                      inspResult={inspResult}
                      row={row}
                      isVisible={visibleAIIndices.includes(index)}
                    />
                    <TableCell sx={{ width: "150px", color: "var(--dg-color)" }}>
                      {formatNumber(Number(row.score), 4)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <></>
              )}

              {projectImagesInfo.length < 1 && (
                <TableRow
                  sx={{
                    backgroundColor: "var(--unselected-row-bg)",
                    height: "58px",
                  }}
                >
                  <TableCell colSpan={columns.length} align="center">
                    {t("NoDataMsg")}
                  </TableCell>
                </TableRow>
              )}

              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div ref={observerBottomTargetRef} style={{ height: "10px" }} />
        </TableContainer>
      </Grid>
    </>
  );
}

interface AITableCellProps {
  inspStatus: InspStatusItem | null;
  inspResult: any[];
  row: IImageInfo;
  isVisible: boolean;
}

const AITableCell = ({ inspStatus, inspResult, row, isVisible }: AITableCellProps) => {
  const rowData = inspResult.find((label) => label.id === row.id);
  const isMatched = !!rowData;

  // inspStatus가 null이거나 상태가 "end"이면 색상 고정
  const shouldUseDefaultColor = !inspStatus || inspStatus.state === "end";

  const textColor = shouldUseDefaultColor
    ? "var(--dg-color)"
    : isMatched
      ? "var(--dg-insp-ai-color)"
      : "var(--dg-color)";

  return (
    <TableCell
      sx={{
        width: "125px",
        color: textColor,
        fontWeight: isMatched && !shouldUseDefaultColor ? 600 : "normal",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(-10px)",
        transition: "opacity 0.2s ease, transform 0.2s ease",
      }}
    >
      {isMatched ? rowData.AILabel : row.AILabel}
    </TableCell>
  );
};
